import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { finalize } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator  } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ServicesService } from 'src/services/services.service';
import { MatDialog } from '@angular/material/dialog';
import { BlogViewDialogComponent } from './blog-view-dialog/blog-view-dialog.component';
import { BlogFormDialogComponent } from './blog-form-dialog/blog-form-dialog.component';

export interface Blog {
  blog_id: string;
  title: string;
  short_description: string;
  description: string;
  image_path: string;
  meta_title: string | null;
  meta_keywords: string | null;
  meta_description: string | null;
  h1_tag: string | null;
  author: string;
  status: string;
  is_active: string;
  created_date: string;
  updated_date: string;
}

export interface BlogApiResponse {
  flag?: boolean;
  data?: Blog[] | Blog;
  blogs?: Blog[];
  msg?: string;
}

@Component({
  selector: 'app-blog-list',
  templateUrl: './blog.component.html',
  standalone: false,
  styleUrls: ['./blog.component.scss']
})
export class BlogComponent implements OnInit, AfterViewInit {
  private readonly blogService = inject(ServicesService);
  private readonly dialog = inject(MatDialog);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  @Output() viewBlog = new EventEmitter<Blog>();
  @Output() editBlog = new EventEmitter<Blog>();
  @Output() createBlog = new EventEmitter<void>();

  readonly displayedColumns: string[] = [
    'blog_id',
    'blog',
    'author',
    'status',
    'is_active',
    'created_date',
    'actions'
  ];

  readonly dataSource = new MatTableDataSource<Blog>([]);

  // Replace with your real image directory.
  readonly imageBaseUrl = 'https://aieventzo.com/upload/blog/';

  searchText = '';
  selectedStatus = 'all';
  loading = false;
  errorMessage = '';

  ngOnInit(): void {
    this.configureFiltering();
    this.loadBlogs();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadBlogs(): void {
    this.loading = true;
    this.errorMessage = '';

    this.blogService.getBlog()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.dataSource.data = this.normalizeBlogs(response);

          // Reapply the current filters after refreshing.
          this.applyFilters();
        },
        error: (error) => {
          console.error('Failed to load blogs:', error);
          this.errorMessage = 'Unable to load blogs. Please try again.';
        }
      });
  }

  private normalizeBlogs(
    response: BlogApiResponse | Blog[] | Blog
  ): Blog[] {
    if (Array.isArray(response)) {
      return response;
    }

    if ('blog_id' in response) {
      return [response];
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (response.data && 'blog_id' in response.data) {
      return [response.data];
    }

    return response.blogs ?? [];
  }

  private configureFiltering(): void {
    this.dataSource.filterPredicate = (blog, filter) => {
      const criteria = JSON.parse(filter) as {
        search: string;
        status: string;
      };

      const searchableText = [
        blog.blog_id,
        blog.title,
        blog.author,
        blog.short_description,
        blog.status,
        blog.is_active
      ]
        .map(value => this.stripHtml(String(value ?? '')))
        .join(' ')
        .toLowerCase();

      const matchesSearch = searchableText.includes(criteria.search);

      const matchesStatus =
        criteria.status === 'all' ||
        blog.status?.toLowerCase() === criteria.status;

      return matchesSearch && matchesStatus;
    };

    this.dataSource.sortingDataAccessor = (blog, property) => {
      switch (property) {
        case 'blog_id':
          return Number(blog.blog_id);

        case 'blog':
          return blog.title?.toLowerCase() ?? '';

        case 'author':
          return blog.author?.toLowerCase() ?? '';

        case 'status':
          return blog.status?.toLowerCase() ?? '';

        case 'is_active':
          return blog.is_active === '1' ? 1 : 0;

        case 'created_date':
          return new Date(blog.created_date).getTime() || 0;

        default:
          return '';
      }
    };
  }

  applyFilters(): void {
    this.dataSource.filter = JSON.stringify({
      search: this.searchText.trim().toLowerCase(),
      status: this.selectedStatus
    });

    this.dataSource.paginator?.firstPage();
  }

  stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  getExcerpt(value: string, maxLength = 110): string {
    const text = this.stripHtml(value);

    return text.length > maxLength
      ? `${text.slice(0, maxLength).trimEnd()}...`
      : text;
  }

  get publishedCount(): number {
    return this.dataSource.data.filter(
      blog => blog.status?.toLowerCase() === 'published'
    ).length;
  }

  get inactiveCount(): number {
    return this.dataSource.data.filter(
      blog => blog.is_active !== '1'
    ).length;
  }

  get activeCount(): number {
    return this.dataSource.data.filter(
      blog => blog.is_active === '1'
    ).length;
  }

  isPublished(status: string): boolean {
    return status?.toLowerCase() === 'published';
  }

  isActive(value: string): boolean {
    return value === '1';
  }

  onView(blog: Blog): void {
    this.dialog.open(BlogViewDialogComponent, {
      data: blog,
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'blog-view-dialog-panel'
    });
  }

  onCreate(): void {
    this.openBlogForm('add');
  }

  onEdit(blog: Blog): void {
    this.openBlogForm('edit', blog);
  }

  private openBlogForm(
    mode: 'add' | 'edit',
    blog?: Blog
  ): void {
    const dialogRef = this.dialog.open(BlogFormDialogComponent, {
      width: '850px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: true,
      disableClose: true,
      panelClass: 'blog-form-dialog-panel',
      data: {
        mode,
        blog
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.saved) {
        this.loadBlogs();
      }
    });
  }

  onDelete(blog: Blog): void {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${blog.title}"?`
    );

    if (!confirmed) {
      return;
    }

    this.blogService.deleteBlog(blog.blog_id).subscribe({
      next: () => {
        this.dataSource.data = this.dataSource.data.filter(
          item => item.blog_id !== blog.blog_id
        );

        this.applyFilters();
      },
      error: (error) => {
        console.error('Failed to delete blog:', error);
        this.errorMessage = 'Unable to delete this blog. Please try again.';
      }
    });
  }
}